import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useRestaurant } from "../contexts/RestaurantContext";
import {
    setRestaurantData,
    setCompanySettings,
    setRestaurantFetchStatus,
    selectRestaurantData,
    selectRestaurantFetchMeta,
} from "../app/restaurantSlice";
import onlineOrderingService from "../services/onlineOrderingService";
import { getRestaurantFetchErrorMessage } from "../utils/restaurantFetchErrors";

/**
 * Loads restaurant + company settings for the current slug (layout-level bootstrap).
 */
export function useRestaurantBootstrap() {
    const dispatch = useDispatch();
    const store = useStore();
    const { restaurantSlug, locationId } = useRestaurant();
    const [retryNonce, setRetryNonce] = useState(0);

    const restaurantData = useSelector((state) =>
        restaurantSlug ? selectRestaurantData(state, restaurantSlug) : null
    );
    const fetchMeta = useSelector((state) =>
        restaurantSlug ? selectRestaurantFetchMeta(state, restaurantSlug) : { status: "idle", error: null }
    );

    const runFetch = useCallback(async () => {
        if (!restaurantSlug) {
            return;
        }

        const cached = selectRestaurantData(store.getState(), restaurantSlug);
        if (!cached) {
            dispatch(
                setRestaurantFetchStatus({
                    restaurantSlug,
                    status: "loading",
                    error: null,
                })
            );
        }

        try {
            const [data, settings] = await Promise.all([
                onlineOrderingService.getRestaurantBySlug(restaurantSlug, locationId),
                onlineOrderingService.getCompanySettings(restaurantSlug).catch((err) => {
                    console.warn("Failed to fetch company settings:", err);
                    return null;
                }),
            ]);

            dispatch(setRestaurantData({ restaurantSlug, restaurantData: data }));

            if (settings) {
                dispatch(setCompanySettings({ restaurantSlug, settingsData: settings }));
                // Keep Contact pane in sync: attach dedicated OO contacts onto restaurant data.
                const ooPhone = (
                    settings.s_OnlineOrderingPhone ||
                    settings.S_OnlineOrderingPhone ||
                    settings.onlineOrderingPhone ||
                    ""
                ).toString().trim();
                const ooEmail = (
                    settings.s_OnlineOrderingEmail ||
                    settings.S_OnlineOrderingEmail ||
                    settings.onlineOrderingEmail ||
                    ""
                ).toString().trim();
                if (ooPhone || ooEmail) {
                    dispatch(
                        setRestaurantData({
                            restaurantSlug,
                            restaurantData: {
                                ...(data || {}),
                                onlineOrderingPhone: ooPhone || data?.onlineOrderingPhone || "",
                                onlineOrderingEmail: ooEmail || data?.onlineOrderingEmail || "",
                            },
                        })
                    );
                }
            }

            dispatch(
                setRestaurantFetchStatus({
                    restaurantSlug,
                    status: "succeeded",
                    error: null,
                })
            );
        } catch (err) {
            console.error("Restaurant bootstrap failed:", err);
            const stillCached = selectRestaurantData(store.getState(), restaurantSlug);
            if (stillCached) {
                dispatch(
                    setRestaurantFetchStatus({
                        restaurantSlug,
                        status: "succeeded",
                        error: null,
                    })
                );
            } else {
                dispatch(
                    setRestaurantFetchStatus({
                        restaurantSlug,
                        status: "failed",
                        error: getRestaurantFetchErrorMessage(err),
                    })
                );
            }
        }
    }, [dispatch, restaurantSlug, locationId, store]);

    useEffect(() => {
        if (!restaurantSlug) return;

        const cached = selectRestaurantData(store.getState(), restaurantSlug);
        if (cached) {
            dispatch(
                setRestaurantFetchStatus({
                    restaurantSlug,
                    status: "succeeded",
                    error: null,
                })
            );
        }

        runFetch();
    }, [restaurantSlug, locationId, retryNonce, runFetch, dispatch, store]);

    const retry = useCallback(() => {
        if (!restaurantSlug) return;
        dispatch(
            setRestaurantFetchStatus({
                restaurantSlug,
                status: "loading",
                error: null,
            })
        );
        setRetryNonce((n) => n + 1);
    }, [dispatch, restaurantSlug]);

    const hasData = Boolean(restaurantData);
    const status = hasData ? "succeeded" : fetchMeta.status;
    const isLoading = status === "loading" && !hasData;
    const isFailed = status === "failed" && !hasData;

    return {
        restaurantSlug,
        restaurantData,
        status,
        error: fetchMeta.error,
        isLoading,
        isFailed,
        isReady: hasData,
        retry,
    };
}
